import {
  type AuditEventPage,
  type BeginAuthenticatorEnrollmentResponse,
  type LoginOutcome,
  type LoginResponse,
  type CompanyTimeZoneResponse,
  type MeResponse,
  type MembershipResponse,
  type MfaEnrollmentResponse,
  type PermissionResponse,
  type RoleResponse,
  type TrustedDeviceResponse,
} from '../types';
import type { IdentityGateway } from './IdentityGateway';
import { refreshAccessToken } from '../../../shared/tokenRefresh';
import { apiErrorFromResponse } from '../../../shared/apiErrorFromResponse';

/**
 * Talks to the real API. Every call uses `credentials: 'include'` so the httpOnly access/refresh
 * cookies the API sets are sent back automatically — there is no token to read, store, or attach by
 * hand anywhere in this class, by design (see AuthCookies.cs on the backend).
 */
export class HttpIdentityGateway implements IdentityGateway {
  constructor(private readonly baseUrl: string) {}

  // `signal` is a separate parameter, not folded into `init`, because RequestInit.signal is typed as
  // `AbortSignal | null` and this project's exactOptionalPropertyTypes rejects assigning it a plain
  // `undefined` — building the init object conditionally here keeps every call site simple.
  private async request<T>(path: string, init?: RequestInit, signal?: AbortSignal): Promise<T> {
    const doFetch = () =>
      fetch(`${this.baseUrl}${path}`, {
        ...init,
        ...(signal ? { signal } : {}),
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...init?.headers },
      });

    let response = await doFetch();

    // The access-token cookie is short-lived (15 min) — a 401 here is very often just that it expired
    // mid-session, not a real auth failure (this was reported live as "switch-company failed with 401"
    // after an idle session, but the same gap applied to every authenticated call). `login()` no longer
    // goes through this helper at all (see its own doc comment), so there is no "/api/auth/login" special
    // case to carve out here any more.
    if (response.status === 401 && (await refreshAccessToken())) {
      response = await doFetch();
    }

    if (!response.ok) {
      throw await apiErrorFromResponse(response, `Request to ${path} failed with ${response.status}`);
    }

    // Results.Ok() with no value (logout, switch-company's antecedents, etc.) sends status 200 with a
    // genuinely empty body, not 204 — response.json() throws "Unexpected end of JSON input" on that empty
    // string, so an empty body is treated as "no value" regardless of which 2xx status carried it.
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  async login(email: string, password: string, rememberMe = false): Promise<LoginOutcome> {
    // Not built on the shared `request()` helper below: a 423 (locked) carries a real, meaningful JSON
    // body (`{ accountLocked, lockoutEndUtc }`, no `detail` field) that must be returned to the caller,
    // not thrown as an ApiError, and `request()` treats every non-2xx as a throw. The existing "never
    // retry a 401 here" rule (a 401 from /login means wrong credentials, not an expired token) is
    // preserved by simply never calling refreshAccessToken() in this method at all.
    const response = await fetch(`${this.baseUrl}/api/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, rememberMe }),
    });

    if (response.status === 423) {
      const body = (await response.json()) as { lockoutEndUtc: string };
      return { kind: 'locked', lockoutEndUtc: body.lockoutEndUtc };
    }

    if (!response.ok) {
      throw await apiErrorFromResponse(response, `Request to /api/auth/login failed with ${response.status}`);
    }

    const body = (await response.json()) as
      | { userId: string; email: string; memberships: LoginResponse['memberships'] }
      | { challengeToken: string; availableMethods: string[] };

    if ('challengeToken' in body) {
      return { kind: 'mfaRequired', challengeToken: body.challengeToken, availableMethods: body.availableMethods };
    }
    return { kind: 'success', userId: body.userId, email: body.email, memberships: body.memberships };
  }

  async logout(): Promise<void> {
    await this.request('/api/auth/logout', { method: 'POST' });
  }

  mfaChallenge(challengeToken: string, method: string): Promise<void> {
    return this.request('/api/auth/mfa/challenge', { method: 'POST', body: JSON.stringify({ challengeToken, method }) });
  }

  mfaVerify(challengeToken: string, method: string, code: string, rememberDevice: boolean): Promise<LoginResponse> {
    return this.request('/api/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ challengeToken, method, code, rememberDevice }),
    });
  }

  async forgotPassword(email: string): Promise<void> {
    await this.request('/api/auth/password/forgot', { method: 'POST', body: JSON.stringify({ email }) });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.request('/api/auth/password/reset', { method: 'POST', body: JSON.stringify({ token, newPassword }) });
  }

  async validateResetToken(token: string): Promise<boolean> {
    const result = await this.request<{ valid: boolean }>(
      `/api/auth/password/reset/validate?token=${encodeURIComponent(token)}`,
    );
    return result.valid;
  }

  async requestUnlock(email: string): Promise<void> {
    await this.request('/api/auth/unlock/request', { method: 'POST', body: JSON.stringify({ email }) });
  }

  async completeUnlock(token: string): Promise<void> {
    await this.request('/api/auth/unlock/complete', { method: 'POST', body: JSON.stringify({ token }) });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.request('/api/me/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  getMe(signal?: AbortSignal): Promise<MeResponse> {
    return this.request('/api/me', undefined, signal);
  }

  switchCompany(membershipId: string): Promise<{ roleId: string }> {
    return this.request('/api/me/switch-company', { method: 'POST', body: JSON.stringify({ membershipId }) });
  }

  setCompanyTimeZone(timeZoneId: string): Promise<CompanyTimeZoneResponse> {
    return this.request('/api/company-settings/time-zone', { method: 'PUT', body: JSON.stringify({ timeZoneId }) });
  }

  listRoles(signal?: AbortSignal): Promise<RoleResponse[]> {
    return this.request('/api/roles', undefined, signal);
  }

  listGrantsForRole(roleId: string, signal?: AbortSignal): Promise<PermissionResponse[]> {
    return this.request(`/api/roles/${roleId}/permissions`, undefined, signal);
  }

  listMembers(companyId: string, signal?: AbortSignal): Promise<MembershipResponse[]> {
    return this.request(`/api/companies/${companyId}/memberships`, undefined, signal);
  }

  async revokeMember(companyId: string, membershipId: string): Promise<void> {
    await this.request(`/api/companies/${companyId}/memberships/${membershipId}`, { method: 'DELETE' });
  }

  changeMembershipRole(companyId: string, membershipId: string, roleId: string): Promise<MembershipResponse> {
    return this.request(`/api/companies/${companyId}/memberships/${membershipId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ roleId }),
    });
  }

  getAuditTrail(page: number, pageSize: number, signal?: AbortSignal): Promise<AuditEventPage> {
    return this.request(`/api/audit-events?page=${page}&pageSize=${pageSize}`, undefined, signal);
  }

  getEmailDelivery(signal?: AbortSignal): Promise<{ enabled: boolean }> {
    return this.request('/api/me/email-delivery', undefined, signal);
  }

  listMfaEnrollments(signal?: AbortSignal): Promise<MfaEnrollmentResponse[]> {
    return this.request('/api/me/mfa-enrollments', undefined, signal);
  }

  beginAuthenticatorEnrollment(): Promise<BeginAuthenticatorEnrollmentResponse> {
    return this.request('/api/me/mfa-enrollments/authenticator/begin', { method: 'POST' });
  }

  confirmAuthenticatorEnrollment(secret: string, code: string): Promise<MfaEnrollmentResponse> {
    return this.request('/api/me/mfa-enrollments/authenticator/confirm', {
      method: 'POST',
      body: JSON.stringify({ secret, code }),
    });
  }

  async disableAuthenticatorEnrollment(): Promise<void> {
    await this.request('/api/me/mfa-enrollments/authenticator/disable', { method: 'POST' });
  }

  listTrustedDevices(signal?: AbortSignal): Promise<TrustedDeviceResponse[]> {
    return this.request('/api/me/trusted-devices', undefined, signal);
  }

  async revokeTrustedDevice(deviceId: string): Promise<void> {
    await this.request(`/api/me/trusted-devices/${deviceId}`, { method: 'DELETE' });
  }

  async requestStepUp(): Promise<void> {
    await this.request('/api/me/step-up/request', { method: 'POST' });
  }

  async verifyStepUp(code: string): Promise<void> {
    await this.request('/api/me/step-up/verify', { method: 'POST', body: JSON.stringify({ code }) });
  }
}
